import { useState } from 'react';
import type { Ctx } from '../../ctx';
import { companyOf, durationMs, fmt, fmtMs, matchesQuery, screensOf } from '../../model';
import { StatusChip, StepChips } from '../../ui';
import type { Pipeline, StepRecord, TtsRow } from '../../types';

/** The Logs-tab probe: always dry-run, defaults everywhere. The Tools page has the full form (and the LIVE option). */
const QUICK_PROBE = { requestType: 'sales', screens: '1-10', countryTag: '+1', dryRun: true } as const;

const STATUSES = ['done', 'dry-run', 'failed', 'pending', 'skipped'];

function HsLink({ kind, id, portalId }: { kind: string; id: string | null; portalId: string }) {
  if (!id) return <span className="faint">—</span>;
  if (!portalId) return <span className="mono">{id}</span>;
  return (
    <a
      className="mono"
      target="_blank"
      rel="noopener"
      href={`https://app.hubspot.com/contacts/${portalId}/record/${kind}/${id}`}
    >
      {id}
    </a>
  );
}

function stepClass(s: StepRecord): string {
  return s.error ? 'err' : s.would ? 'dry' : s.skipped ? 'skip' : 'ok';
}

function Drawer({ ctx, row, onClose }: { ctx: Ctx; row: TtsRow; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const live = ctx.data.dials?.tts.dryRun === false;
  const replayable = row.status !== 'done';
  const replay = async (dryRun: boolean): Promise<void> => {
    setBusy(true);
    try {
      const r = await ctx.data.replayTts(row.submission_key, dryRun);
      const o = r.outcome;
      ctx.toast(`Replay ${row.submission_key} → ${o.status}${o.skipped ? `:${o.skipped}` : ''}${o.error ? ` — ${o.error}` : ''}`);
    } catch (e) {
      ctx.toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong className="mono">{row.submission_key}</strong>
          <button type="button" className="btn sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="row" style={{ margin: '8px 0 14px' }}>
          <StatusChip status={row.status} />
          {row.dry_run ? <span className="chip info">dry-run</span> : null}
          <span className="chip">{row.request_type}</span>
          <span className="chip mono">{row.region}</span>
          <span className="dim">
            {fmt(row.first_seen)} · {row.attempts} attempt{row.attempts > 1 ? 's' : ''}
          </span>
        </div>
        {row.error ? (
          <div className="notice" style={{ marginBottom: 12 }}>
            <strong>error:</strong> <code>{row.error}</code>
          </div>
        ) : null}
        <dl className="kv" style={{ marginBottom: 14 }}>
          <dt>email</dt>
          <dd>{row.email}</dd>
          <dt>company</dt>
          <dd>{companyOf(row) || '—'}</dd>
          <dt>screens</dt>
          <dd className="mono">
            {screensOf(row)}
            {row.d_deal ? ' · D deal' : ''}
          </dd>
          <dt>contact</dt>
          <dd>
            <HsLink kind="0-1" id={row.contact_id} portalId={ctx.portalId} />
          </dd>
          <dt>note</dt>
          <dd>
            <HsLink kind="0-1" id={row.note_id} portalId={ctx.portalId} />
          </dd>
          {row.request_type === 'support' ? (
            <>
              <dt>ticket</dt>
              <dd>
                <HsLink kind="0-5" id={row.ticket_id} portalId={ctx.portalId} />
              </dd>
              <dt>ticket note</dt>
              <dd>
                <HsLink kind="0-1" id={row.ticket_note_id} portalId={ctx.portalId} />
              </dd>
            </>
          ) : null}
          {row.cio_delivery_id ? (
            <>
              <dt>cio delivery</dt>
              <dd className="mono">{row.cio_delivery_id}</dd>
            </>
          ) : null}
        </dl>
        <h2>Steps</h2>
        {Object.entries(row.steps ?? {}).map(([n, s]) => (
          <div className="well" style={{ marginBottom: 8 }} key={n}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="mono">
                <strong>{n}</strong>{' '}
                <span className={`st ${stepClass(s)}`}>
                  {s.error ? 'error' : s.would ? 'would' : s.skipped ? `skipped: ${s.skipped}` : 'ok'}
                </span>
              </span>
              <span className="mono dim" style={{ fontSize: 11 }}>
                {fmt(s.at)}
              </span>
            </div>
            {s.error ? <pre style={{ marginTop: 6 }}>{s.error}</pre> : null}
            {s.would ? <pre style={{ marginTop: 6 }}>{JSON.stringify(s.would, null, 2)}</pre> : null}
            {s.result ? (
              <div className="note" style={{ marginTop: 4 }}>
                result <code>{JSON.stringify(s.result)}</code>
              </div>
            ) : null}
          </div>
        ))}
        <h2 style={{ marginTop: 14 }}>Form payload</h2>
        <pre>{JSON.stringify(row.payload, null, 2)}</pre>
        <div className="row" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="btn primary"
            disabled={!ctx.admin || busy || !replayable}
            title={!ctx.admin ? 'admin only' : !replayable ? 'a done row has nothing left to run' : undefined}
            onClick={() => void replay(false)}
          >
            {busy ? 'Replaying…' : live ? 'Replay (LIVE)' : 'Replay'}
          </button>
          {live ? (
            <button
              type="button"
              className="btn"
              disabled={!ctx.admin || busy || !replayable}
              title="records `would` for the missing steps, writes nothing"
              onClick={() => void replay(true)}
            >
              Replay as dry-run
            </button>
          ) : null}
          <button
            type="button"
            className="btn"
            onClick={() => {
              try {
                void navigator.clipboard?.writeText(row.submission_key);
              } catch {
                /* clipboard unavailable */
              }
              ctx.toast(`Copied ${row.submission_key}`);
            }}
          >
            Copy key
          </button>
          <span className="note">
            Replay re-runs the stored payload under the current dial ({live ? 'LIVE — missing steps write' : 'dry-run'}); steps
            already <code>ok</code> are skipped.
          </span>
        </div>
      </aside>
    </>
  );
}

export default function LogsTab({ ctx, pipeline }: { ctx: Ctx; pipeline: Pipeline }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [probing, setProbing] = useState(false);
  const { data } = ctx;

  const quickProbe = async (): Promise<void> => {
    setProbing(true);
    try {
      const r = await data.sendProbe(QUICK_PROBE);
      ctx.toast(r.accepted ? `Probe ${r.key} accepted (dry-run) — reloading` : `Probe rejected by our route: HTTP ${r.status}`);
      window.setTimeout(() => data.reload(), 1500); // processing is async; the row exists by then
    } catch (e) {
      ctx.toast(e instanceof Error ? e.message : String(e));
    } finally {
      setProbing(false);
    }
  };

  if (pipeline.key === 'calendly') {
    return (
      <section className="card">
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>id</th>
                <th>Created</th>
                <th>Kind</th>
                <th>Invitee</th>
                <th>Event</th>
                <th>Host</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.calendly.map((r) => (
                <tr key={r.id}>
                  <td className="tab">{r.id}</td>
                  <td className="mono dim">{fmt(r.created_at)}</td>
                  <td className="mono">{r.kind}</td>
                  <td>{r.invitee_email}</td>
                  <td>{r.event_name}</td>
                  <td>{r.host_email}</td>
                  <td>
                    {r.status === 'held' ? (
                      <span className="chip warn">held · {r.hold_reason}</span>
                    ) : (
                      <StatusChip status={r.status} />
                    )}
                  </td>
                </tr>
              ))}
              {!data.calendly.length ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty">{data.loading ? 'loading…' : 'No rows yet.'}</div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <p className="note" style={{ padding: '10px 16px', margin: 0 }}>
          Read-only. Rows stay until the removal MR.
        </p>
      </section>
    );
  }

  const list = data.tts.filter((r) => matchesQuery(r, q) && (!status || r.status === status));
  const row = open ? data.tts.find((r) => r.submission_key === open) : undefined;

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <input
          type="search"
          aria-label="search rows"
          placeholder="email · key · company"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ minWidth: 220 }}
        />
        <select aria-label="status filter" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">any status</option>
          {STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <span className="dim">{list.length} rows</span>
        <span className="grow" />
        <button
          type="button"
          className="btn sm"
          disabled={!ctx.admin || probing}
          title={ctx.admin ? 'signed synthetic submission, always dry-run (Tools has the full form)' : 'admin only'}
          onClick={() => void quickProbe()}
        >
          {probing ? 'Sending…' : 'Send test probe'}
        </button>
      </div>
      <section className="card">
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Received</th>
                <th>Email</th>
                <th>Type</th>
                <th>Region</th>
                <th>Screens</th>
                <th>Status</th>
                <th>Steps</th>
                <th>Att.</th>
                <th>ms</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr
                  key={r.submission_key}
                  className={`click ${open === r.submission_key ? 'sel' : ''}`}
                  onClick={() => setOpen(r.submission_key)}
                >
                  <td className="mono dim">{fmt(r.first_seen)}</td>
                  <td>
                    {r.email}
                    {r.attempts > 1 ? (
                      <span className="chip mono" title="retried">
                        ×{r.attempts}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {r.request_type === 'support' ? (
                      <span className="chip info">support</span>
                    ) : (
                      <span className="chip">{r.request_type}</span>
                    )}
                  </td>
                  <td className="mono">{r.region}</td>
                  <td className="mono">
                    {screensOf(r)}
                    {r.d_deal ? (
                      <span className="chip ok" title="tts_d_deal">
                        D
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <StatusChip status={r.status} />
                  </td>
                  <td>
                    <StepChips steps={r.steps} />
                  </td>
                  <td className="tab dim">{r.attempts}</td>
                  <td className="tab dim" title="first_seen → last step (spans every attempt)">
                    {fmtMs(durationMs(r))}
                  </td>
                </tr>
              ))}
              {!list.length ? (
                <tr>
                  <td colSpan={9}>
                    <div className="empty">{data.loading ? 'loading…' : 'No rows match.'}</div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
      <p className="note">
        One row per submission; every step's outcome under <code>steps.&lt;name&gt;</code>. Click a
        row for the step records, the <code>would</code> payloads and Replay. Search filters the{' '}
        {data.tts.length} newest rows loaded.
      </p>
      {row ? <Drawer ctx={ctx} row={row} onClose={() => setOpen(null)} /> : null}
    </>
  );
}
